// src/pages/Home.tsx
import "../styles/Home.css";
import React, { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { supabase, SUPABASE_URL } from "../lib/supabaseClient";
import logo from "../assets/Logo.png";

import {
  ensureList,
  replaceProducts,
  setPublished,
  getPublishedListBySlug,
  type PriceList,
} from "../utils/api";
import { readPriceFile } from "../utils/excel";
import { dlog as _dlog, dtoast as _dtoast } from "../utils/debug";

const FIXED_SLUG =
  (import.meta.env.VITE_PUBLIC_LIST_SLUG as string | undefined)?.trim() ||
  "lista-actual";

const dlog = _dlog ?? ((...a: any[]) => console.log(...a));
const dtoast = _dtoast ?? ((msg: string) => alert(msg));

type Progress = { done: number; total: number };

/* ===== Helpers de formato ===== */
function toNumberAR(v: unknown): number {
  if (v === null || v === undefined) return 0;
  let s = String(v).trim();
  if (s === "") return 0;
  s = s.replace(/[^\d,.\-]+/g, "");
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    s = lastComma > lastDot ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  } else if (hasComma) {
    s = s.replace(",", ".");
  } else if (hasDot && /\d{1,3}(\.\d{3})+(\.0+)?$/.test(s)) {
    s = s.replace(/\./g, "");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function fmtMoney(n?: number | null) {
  const val = !n || Number.isNaN(n) ? 0 : n;
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 2,
  }).format(val);
}

export default function Home() {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [list, setList] = useState<PriceList | null>(null);
  const [published, setPublishedFlag] = useState(false);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<Progress>({ done: 0, total: 0 });

  const percent = useMemo(() => {
    if (!progress.total) return 0;
    return Math.round((progress.done / progress.total) * 100);
  }, [progress]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const qrBoxRef = useRef<HTMLDivElement | null>(null);

  // ====== Estadísticas del Excel (con TOTALES en $) ======
  const stats = useMemo(() => {
    const total = rows.length;
    const labs = new Set<string>();
    const rubros = new Set<string>();
    let sumPvp = 0;
    let sumNoTax = 0;

    for (const r of rows) {
      if (r?.lab) labs.add(String(r.lab).trim());
      if (r?.rubro) rubros.add(String(r.rubro).trim());
      if (r?.pvp !== undefined && r?.pvp !== null && String(r.pvp) !== "") {
        sumPvp += toNumberAR(r.pvp);
      }
      if (r?.sin_impuestos !== undefined && r?.sin_impuestos !== null && String(r.sin_impuestos) !== "") {
        sumNoTax += toNumberAR(r.sin_impuestos);
      }
    }
    return {
      total,
      labs: labs.size,
      rubros: rubros.size,
      sumPvp,
      sumNoTax,
    };
  }, [rows]);

  // ====== Cargar archivo ======
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setRows([]);
    setError(null);
    if (!f) return;

    setInfo("Leyendo archivo…");
    try {
      const parsed = await readPriceFile(f);
      setRows(parsed);
      dtoast(`Archivo leído correctamente (${parsed.length} filas).`);
    } catch (err: any) {
      setError(err?.message ?? "No se pudo leer el archivo.");
    } finally {
      setInfo("");
    }
  }

  // ====== Subir datos (reemplazo total automático) ======
  async function handleUpload() {
    if (!file) return dtoast("Elegí un archivo Excel primero.");
    if (!rows.length) return dtoast("El Excel no tiene filas válidas.");

    setLoading(true);
    setError(null);
    setInfo("Preparando lista…");
    setProgress({ done: 0, total: rows.length });

    try {
      const created = await ensureList(FIXED_SLUG, "Lista de precios");
      setList(created);

      // Reemplazo atómico: borra todo y sube
      const CHUNK = 800;
      await replaceProducts(created.id, rows, CHUNK, (inserted, total) =>
        setProgress({ done: inserted, total })
      );

      dtoast(`Archivo subido correctamente (${rows.length} filas).`);
    } catch (err: any) {
      setError(err?.message ?? "Error subiendo datos.");
    } finally {
      setLoading(false);
      setInfo("");
    }
  }

  // ====== Publicar / Ocultar ======
  async function onTogglePublish() {
    if (!list) return dtoast("Primero subí y guardá una lista.");
    try {
      const next = !published;
      await setPublished(FIXED_SLUG, next);
      const refreshed = await getPublishedListBySlug(FIXED_SLUG);
      setPublishedFlag(!!refreshed?.published);
      setPublishedAt(refreshed?.published_at ?? null);
      dtoast(next ? "Lista publicada" : "Lista ocultada");
    } catch (err: any) {
      setError(err?.message ?? "Error cambiando estado de publicación.");
    }
  }

  // ====== Leer estado ======
  async function onLoadCurrent() {
    try {
      const l = await getPublishedListBySlug(FIXED_SLUG);
      setList(l);
      setPublishedFlag(!!l?.published);
      setPublishedAt(l?.published_at ?? null);
      dtoast("Estado cargado correctamente.");
    } catch (err: any) {
      setError(err?.message ?? "Error leyendo estado.");
    }
  }

  // ====== Descargar QR ======
  async function downloadQR() {
    const svg = qrBoxRef.current?.querySelector("svg") as SVGSVGElement | null;
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgData = serializer.serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width * 4;
      canvas.height = img.height * 4;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = "qr-lista.png";
      link.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  }

  // ====== Logout ======
  async function onLogout() {
    await supabase.auth.signOut();
    window.location.assign("/login");
  }

  // Base robusta: VITE_PUBLIC_BASE_URL o BASE_URL (respeta subpath)
  const computedBase =
    (import.meta.env.VITE_PUBLIC_BASE_URL as string | undefined)?.replace(/\/+$/, "") ||
    new URL(import.meta.env.BASE_URL || "/", window.location.origin).href.replace(/\/+$/, "");
  const publicUrl = `${computedBase}/l/${FIXED_SLUG}`;

  return (
    <div className="admin-wrap">
      {/* Header */}
      <header className="admin-header">
        <img src={logo} alt="Logo" className="admin-logo" />
        <div>
          <h2 className="admin-title">Administrador de Lista</h2>
          <div className="admin-sub">Cargá tu Excel y publicá la lista para tus clientes</div>
        </div>
        <button className="logout-btn" onClick={onLogout} title="Cerrar sesión">
          <span className="logout-icon" />
        </button>
      </header>

      {/* Uploader */}
      <section className="card upload-card">
        <div className="uploader">
          <label htmlFor="fileInput" className="file-label">
            <span className="file-dot" />
            Elegir archivo
          </label>
          <input
            ref={inputRef}
            id="fileInput"
            type="file"
            accept=".xlsx,.xls"
            onChange={onPickFile}
            hidden
          />
          <span className="file-name">
            {file
              ? `${file.name} — filas detectadas: ${rows.length.toLocaleString("es-AR")}`
              : "Ningún archivo seleccionado"}
          </span>
        </div>

        {error && (
          <div className="alert-error mt-10">
            ⚠️ {error}
            <div className="mt-6" style={{ fontSize: 13 }}>
              Sugerencia: verificá que estés logueado y que la lista tenga{" "}
              <code>owner = auth.uid()</code>.
            </div>
          </div>
        )}
      </section>

      {/* Estado + QR */}
      <section className="card pub-card">
        <div className="pub-grid">
          {/* IZQUIERDA */}
          <div className="pub-left">

            {/* CLUSTER CENTRADO */}
            <div className="action-cluster">
              <div className="pub-link-row">
                <button
                  className="chip"
                  onClick={() => window.open(publicUrl, "_blank")}
                >
                  <span className="chip-icon ext" />
                  Abrir lista pública
                </button>
                <button
                  className="chip chip-ghost"
                  onClick={() => navigator.clipboard.writeText(publicUrl)}
                >
                  <span className="chip-icon copy" />
                  Copiar enlace
                </button>
              </div>

              <div className="action-row">
                <button className="btn" onClick={handleUpload} disabled={loading}>
                  Subir archivo
                </button>
                <button className="btn" onClick={onLoadCurrent} disabled={loading}>
                  Leer estado
                </button>
                <button
                  className="btn"
                  onClick={onTogglePublish}
                  disabled={loading || !list}
                >
                  {published ? "Ocultar lista" : "Publicar lista"}
                </button>
              </div>
            </div>

            {/* DETALLES DEL EXCEL */}
            {(file || rows.length > 0) && (
              <div className="file-stats">
                <div className="file-stats-title">Detalle del archivo</div>
                <div className="file-stats-grid">
                  <div className="stat">
                    <div className="stat-label">Archivo</div>
                    <div className="stat-value" title={file?.name || ""}>
                      {file?.name || "-"}
                    </div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Filas</div>
                    <div className="stat-value">{stats.total.toLocaleString("es-AR")}</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Laboratorios únicos</div>
                    <div className="stat-value">{stats.labs.toLocaleString("es-AR")}</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Rubros únicos</div>
                    <div className="stat-value">{stats.rubros.toLocaleString("es-AR")}</div>
                  </div>
                  {/* Totales en $ */}
                  <div className="stat">
                    <div className="stat-label">Total con precio</div>
                    <div className="stat-value">{fmtMoney(stats.sumPvp)}</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Total sin impuestos</div>
                    <div className="stat-value">{fmtMoney(stats.sumNoTax)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* DERECHA: QR + Slug arriba + Botón abajo */}
          <div className="qr-card" ref={qrBoxRef}>
            <QRCodeSVG value={publicUrl} size={180} />
            {/* Slug y fecha ARRIBA del botón */}
            <p className="slug-text">
              Slug: <code>{FIXED_SLUG}</code>
              {publishedAt ? ` · ${new Date(publishedAt).toLocaleString("es-AR")}` : ""}
            </p>
            {/* Botón ABAJO */}
            <button className="chip chip-primary qr-download" onClick={downloadQR}>
              <span className="chip-icon dl" />
              Descargar QR
            </button>
          </div>
        </div>
      </section>

      <footer className="admin-footer">Conectado a: {SUPABASE_URL}</footer>
    </div>
  );
}
